import React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Typography } from "../typography/typography";

interface ReviewCardProps {
  name: string;
  location: string;
  image: string;
  comment: string;
  date: string;
  destination: string;
  rating?: number | null;
  className?: string;
  imageContainerClassName?: string;
  isSelected?: boolean;
  contentClassName?: string;
}
const ReviewCard = ({
  name,
  location,
  image,
  comment,
  date,
  rating,
  className,
  imageContainerClassName,
  isSelected,
  contentClassName,
}: ReviewCardProps) => {
  const defaultContentClassName = `relative left-1/2 mt-10 w-[125%] -translate-x-1/2 text-center transition-all duration-500 sm:w-[150%] ${isSelected ? "opacity-100" : "opacity-0"}`;

  // decide which layout to return based on whether flex has been specified
  const isV2Layout = className?.includes("md:flex");

  const imageContent = (
    <div
      className={cn("relative flex justify-center", imageContainerClassName)}>
      <div className={`relative h-full w-full overflow-hidden transition-all`}>
        <div className="relative h-full w-full">
          <div className="absolute inset-0">
            <Image
              src={image || ""}
              alt={name || "Review image"}
              fill
              className="object-cover"
              sizes="100%"
              loading="lazy"
            />
          </div>
          <div className="absolute z-50 mx-auto flex h-full w-full flex-col items-start justify-end gap-0 p-4 pb-5">
            <Typography
              variant="p-lg"
              as="p"
              className="text-xl font-semibold text-white">
              {name}
            </Typography>
            <Typography variant="p-sm" as="p" className="text-white opacity-90">
              {location}
            </Typography>
          </div>
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-black/0 via-black/60 to-black/90" />
    </div>
  );
  const contentSection = (
    <div
      id="review-description"
      className={cn(contentClassName || defaultContentClassName)}>
      <div className="flex items-center justify-center gap-2">
        <Typography variant="p-sm" className="text-gray-500">
          {date}
        </Typography>

        <div className="flex items-center justify-center gap-1 align-middle">
          <Image
            src="/icons/star-03.svg"
            alt="Star"
            width={20}
            height={20}
            className="relative inline-block h-5 w-5 align-text-bottom"
            loading="lazy"
          />
          <span className="text-md font-family-body align-middle font-semibold text-gray-800">
            {rating}
          </span>
        </div>
      </div>
      <Typography
        variant="p-lg"
        className="mt-2 line-clamp-3 px-4 font-semibold text-gray-800">
        &quot;{comment}&quot;
      </Typography>
    </div>
  );

  if (isV2Layout) {
    return (
      <div className={className}>
        {imageContent}
        {contentSection}
      </div>
    );
  }

  return (
    <>
      <div className={cn("relative flex justify-center", className)}>
        <div
          className={`relative h-full w-full overflow-hidden transition-all`}>
          <div className="relative h-full w-full">
            <div className="absolute inset-0">
              <Image
                src={image || ""}
                alt={name || "Review image"}
                fill
                className="object-cover"
                sizes="100%"
              />
            </div>

            <div className="absolute z-50 mx-auto flex h-full w-full flex-col items-start justify-end gap-0 p-4 pb-5">
              <Typography
                variant="p-lg"
                as="p"
                className="text-xl font-semibold text-white">
                {name}
              </Typography>
              <Typography
                variant="p-sm"
                as="p"
                className="text-white opacity-90">
                {location}
              </Typography>
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-black/0 via-black/60 to-black/90" />
      </div>
      {contentSection}
    </>
  );
};

export default ReviewCard;
